import { useParams, Link, Navigate } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, breadcrumbSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { ARTICLES, getArticleBySlug } from "../data/articles";
import { ArrowUpRight, ArrowLeft, Calendar, Clock } from "lucide-react";

const COPY = {
  en: {
    backToBlog: "← Back to blog",
    backHome: "← Back to MUConcierge home",
    ctaBook: "Book a free discovery call",
    relatedTitle: "Continue reading",
    minRead: "min read",
    inThisArticle: "Topics covered",
    finalCtaTitle: "Ready to skip the Allen-key marathon?",
    finalCtaSub: "Book a free 20-minute discovery call — we reply within 4 business hours.",
  },
  de: {
    backToBlog: "← Zurück zum Blog",
    backHome: "← Zurück zur MUConcierge-Startseite",
    ctaBook: "Kostenloses Beratungsgespräch buchen",
    relatedTitle: "Weitere Artikel",
    minRead: "Min. Lesezeit",
    inThisArticle: "Themen im Artikel",
    finalCtaTitle: "Bereit, den Inbusschlüssel-Marathon zu überspringen?",
    finalCtaSub: "Buchen Sie ein kostenloses 20-Minuten-Beratungsgespräch — Antwort innerhalb von 4 Werktagsstunden.",
  },
};

const renderBlock = (block, i) => {
  if (block.type === "h2")
    return (
      <h2 key={i} className="font-serif text-2xl sm:text-3xl text-[#1A201C] tracking-tight mt-12 mb-4">
        {block.text}
      </h2>
    );
  if (block.type === "p")
    return (
      <p key={i} className="text-[#1A201C]/85 leading-[1.85] mb-5 text-[1.05rem]">
        {block.text}
      </p>
    );
  if (block.type === "ul")
    return (
      <ul key={i} className="space-y-2.5 mb-6 mt-2">
        {block.items.map((it, j) => (
          <li key={j} className="flex items-start gap-3 text-[#1A201C]/85">
            <span className="mt-2.5 w-1.5 h-1.5 bg-[#C26D5C] shrink-0" />
            <span className="leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    );
  return null;
};

export default function ArticlePage({ lang = "en" }) {
  const { slug } = useParams();
  const article = getArticleBySlug(lang, slug);
  const t = COPY[lang];
  const { openDialog } = useBookCall();
  const homePath = lang === "de" ? "/de" : "/";
  const blogPath = lang === "de" ? "/de/mehr" : "/more";

  const local = article?.[lang];
  const origin = typeof window !== "undefined" ? window.location.origin : "https://muconcierge.com";
  const fullPath = local ? `${blogPath}/${local.slug}` : blogPath;

  useSeo({
    title: local ? `${local.title} | MUConcierge Blog` : "MUConcierge Blog",
    description: local?.excerpt || "MUConcierge blog",
    path: fullPath,
    lang,
    alternates: {
      en: article?.en?.slug ? `/more/${article.en.slug}` : "/more",
      de: article?.de?.slug ? `/de/mehr/${article.de.slug}` : "/de/mehr",
    },
    jsonLd: local
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: local.title,
            description: local.excerpt,
            url: origin + fullPath,
            image: article.coverImage,
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            inLanguage: lang === "de" ? "de-DE" : "en-US",
            keywords: local.keywords.join(", "),
            author: { "@type": "Organization", name: "MUConcierge", url: origin },
            publisher: {
              "@type": "Organization",
              name: "MUConcierge",
              url: origin,
              logo: { "@type": "ImageObject", url: origin + "/og-image.jpg" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": origin + fullPath },
          },
          breadcrumbSchema([
            { name: lang === "de" ? "Startseite" : "Home", url: origin + homePath },
            { name: lang === "de" ? "Mehr" : "More", url: origin + blogPath },
            { name: local.title, url: origin + fullPath },
          ]),
        ]
      : [],
  });

  if (!article || !local) return <Navigate to={blogPath} replace />;

  const dateLabel = new Date(article.publishedAt).toLocaleDateString(
    lang === "de" ? "de-DE" : "en-GB",
    { year: "numeric", month: "long", day: "numeric" }
  );

  const otherArticles = ARTICLES.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      {/* HEADER */}
      <header className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12 pt-12 md:pt-16 pb-6" data-testid="article-header">
        <Link
          to={blogPath}
          className="inline-flex items-center gap-1 text-xs tag-line text-[#5C635A] hover:text-[#1C3326] mb-6"
          data-testid="article-back-to-blog"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          {t.backToBlog}
        </Link>
        <p className="tag-line text-[#C26D5C] mb-4">{local.category}</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A201C] tracking-tight leading-[1.1]">
          {local.title}
        </h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#5C635A]">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {dateLabel}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {article.readMinutes} {t.minRead}</span>
        </div>
      </header>

      {/* COVER */}
      <div className="max-w-5xl mx-auto px-0 sm:px-8 md:px-12 mb-10" data-testid="article-cover">
        <img src={article.coverImage} alt={article.coverAlt[lang]} className="w-full h-64 sm:h-80 md:h-[28rem] object-cover" />
      </div>

      {/* BODY */}
      <article className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12 pb-16" data-testid="article-body">
        <p className="font-serif italic text-xl text-[#5C635A] mb-8 leading-relaxed border-l-2 border-[#C26D5C] pl-5">
          {local.excerpt}
        </p>
        <div>{local.blocks.map(renderBlock)}</div>

        {local.keywords?.length > 0 && (
          <div className="mt-14 pt-8 border-t border-[#E5E2DC]" data-testid="article-keywords">
            <p className="tag-line text-[#1C3326] mb-3">{t.inThisArticle}</p>
            <div className="flex flex-wrap gap-2">
              {local.keywords.map((k) => (
                <span key={k} className="inline-block bg-[#F5F3EF] border border-[#E5E2DC] px-3 py-1.5 text-xs text-[#5C635A]">
                  {k}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* RELATED */}
      {otherArticles.length > 0 && (
        <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="article-related">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-14">
            <h2 className="font-serif text-2xl text-[#1A201C] tracking-tight mb-6">{t.relatedTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {otherArticles.map((a) => {
                const al = a[lang];
                return (
                  <Link
                    key={a.id}
                    to={`${blogPath}/${al.slug}`}
                    className="group bg-white border border-[#E5E2DC] p-5 hover:border-[#1C3326] transition-colors"
                    data-testid={`article-related-${a.id}`}
                  >
                    <p className="tag-line text-[#C26D5C] mb-2 text-[11px]">{al.category}</p>
                    <p className="font-serif text-lg text-[#1A201C] leading-tight group-hover:text-[#1C3326]">{al.title}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-14 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FAF9F6] tracking-tight leading-[1.1]">{t.finalCtaTitle}</h2>
          <p className="mt-4 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">{t.finalCtaSub}</p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: `article-${lang}-${article.id}`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="mt-8 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="article-cta-final"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
          <div className="mt-6">
            <Link to={homePath} className="text-xs text-[#FAF9F6]/60 underline underline-offset-4 hover:text-[#E8C9B6]" data-testid="article-back-home">
              {t.backHome}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
